import { GTMPush } from '@utilities/gtm';
import { sha256 } from '@utilities/sha-256';
import md5 from 'md5';

import { SubscriptionStoreAddress, SubscriptionStoreUser } from '@/store/subscription/store.types';

import {
    BatchSubscriptionEndpointModel,
    BatchSubscriptionProps,
    BuyerInfo,
    ObjectSubscription,
} from './endpoint-types';

const API_ENDPOINT = '/umbraco/api/ajaxform/submitsubscriptionsform';

const subscriptionBatchApiCall = async ({
    subscriptions,
    userInfo,
    address,
    buyerInfo,
    fcToken,
}: {
    subscriptions: ObjectSubscription[];
    userInfo: SubscriptionStoreUser;
    address?: SubscriptionStoreAddress;
    buyerInfo?: BuyerInfo;
    fcToken: string;
}) => {
    const body: BatchSubscriptionEndpointModel = {
        FirstName: userInfo.firstName || '',
        LastName: userInfo.lastName,
        LastNamePrefix: userInfo.lastNamePrefix || '',
        Email: userInfo.email,
        Website: userInfo.website || '',
        Subscriptions: subscriptions.map((subscription) => ({
            ProjectId: subscription.projectPhaseId || '',
            PropertyId: subscription.propertyTypeId || '',
            AreaId: subscription.areaId || '',
            DistrictId: subscription.districtId || '',
            ConstructionNumberId: subscription.constructionNumberId || '',
        })),
        FcToken: fcToken,
    };

    if (userInfo.gender) body.IsMale = userInfo.gender === 'Mr';

    if (address) {
        body.Phone = address.phoneNumber;
        body.PostalCode = address.zipcode || '';
        body.Street = address.street || '';
        body.HouseNumber = address.houseNumber;
        body.HouseNumberAddition = address.houseNumberAddition || '';
        body.City = address.city;
        body.Country = address.country;
    }

    if (buyerInfo) {
        body.IntendedUsage = buyerInfo.intendedUsage;
        body.PurchaseTimeframe = buyerInfo.purchaseTimeframe;
        body.FinancialCheck = buyerInfo.financialCheck;
        body.Comment = buyerInfo.comment;
    }

    const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    const succeeded = await response.json();

    if (!response.ok || succeeded !== true) {
        throw new Error(`Newsletter subscription failed with status ${response.status}.`);
    }
};

const subscribeToBatchObjects = async (props: BatchSubscriptionProps) => {
    await subscriptionBatchApiCall(props);

    const { userInfo, subscriptions, GTMEventLabel } = props;
    if (!GTMEventLabel) return;

    const BASE_GTM_DATA = {
        event: 'gtm.SubscriptionSubscription',
        eventCategory: 'Subscription',
        email: md5(userInfo.email),
        email_sha_256: await sha256(userInfo.email),
    };

    subscriptions.forEach((subscription) => {
        let eventAction: string;
        let eventLabel = GTMEventLabel;

        switch (subscription.type) {
            case 'area':
            case 'district':
                eventAction = 'Subscribe Gebied';
                break;
            case 'project':
                eventAction = 'Subscribe Gebied';
                break;
            case 'propertyType':
                eventAction = 'Subscribe Woningtype';
                eventLabel = `${subscription.propertyTypeId} ${GTMEventLabel}`;
                break;
            case 'constructionNumber':
                eventAction = 'Subscribe Bouwnummer';
                eventLabel = `${subscription.constructionNumberId} ${GTMEventLabel}`;
                break;
        }

        GTMPush({
            ...BASE_GTM_DATA,
            eventAction,
            eventLabel,
        });
    });
};

export { subscribeToBatchObjects };
